if (!customElements.get('product-variant-sync')) {
  class ProductVariantSync extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      if (!this.productInfo || this.initialized) return;

      this.initialized = true;
      this.productId = String(this.productInfo.dataset.productId || '');
      this.sectionId = String(this.productInfo.dataset.section || '');
      this.variants = this.getVariantData();
      this.variantIds = new Set(this.variants.map((variant) => String(variant.id)));

      const urlVariantId = new URL(window.location.href).searchParams.get('variant');
      const mainVariantId = this.getMainVariantInput()?.value;
      const initialVariantId = this.variantIds.has(String(urlVariantId)) ? urlVariantId : mainVariantId;

      if (initialVariantId) this.synchronize(initialVariantId, 'initial', false);
      this.subscribeToThemeVariantChanges();
    }

    getVariantData() {
      const data = this.querySelector('[data-product-variant-sync-json]');
      if (!data) return [];

      try {
        return JSON.parse(data.textContent);
      } catch (error) {
        return [];
      }
    }

    getMainVariantInput() {
      return this.productInfo.querySelector('.main-product-form input[name="id"]');
    }

    getStickyBar() {
      return document.querySelector(
        `sticky-atc-bar[data-product-id="${CSS.escape(this.productId)}"][data-section="${CSS.escape(this.sectionId)}"]`
      );
    }

    getCardVariantId(card) {
      return String(card.dataset.nvVariantId || card.dataset.variantId || card.dataset.id || '');
    }

    getCardSelectedClass(card) {
      if (card.matches('.gpack-card[data-id]')) return 'is-selected';
      if (card.matches('[data-dry-fruit-pack-selector] [data-variant-id]')) return 'is-selected';

      const baseClass = Array.from(card.classList).find((className) => className.endsWith('-pack-card'));
      return baseClass ? `${baseClass}--selected` : '';
    }

    updateCustomCards(variantId) {
      const cards = this.productInfo.querySelectorAll(
        '[data-nv-variant-id]:not([data-nv-combo-qty]), [data-dry-fruit-pack-selector] [data-variant-id], .gpack-card[data-id]'
      );

      cards.forEach((card) => {
        const selected = this.getCardVariantId(card) === String(variantId);
        const selectedClass = this.getCardSelectedClass(card);
        if (selectedClass) card.classList.toggle(selectedClass, selected);
        card.setAttribute('aria-pressed', String(selected));
      });

      this.productInfo.querySelectorAll('.nv-wellness-pack-shipping-nudge').forEach((nudge) => {
        const selectedCard = Array.from(cards).find(
          (card) => this.getCardVariantId(card) === String(variantId)
        );
        const optionValue = String(selectedCard?.dataset.nvOptionValue || selectedCard?.dataset.option || '').toLowerCase().replace(/\s+/g, '');
        nudge.hidden = !(optionValue.includes('500g') || optionValue.includes('500gm'));
      });
    }

    updateMainForm(variantId) {
      const input = this.getMainVariantInput();
      if (!input || input.value === String(variantId)) return;

      input.value = String(variantId);
      input.setAttribute('value', String(variantId));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateNativePicker(variant, source) {
      if (source === 'native' || source === 'sticky' || source === 'theme') return;

      const picker = this.productInfo.querySelector('variant-selects');
      if (!picker || !variant) return;

      const controls = Array.from(picker.querySelectorAll('fieldset, select'));
      let changedControl;

      variant.options.forEach((optionValue, index) => {
        const control = controls[index];
        if (!control) return;

        if (control.matches('select')) {
          if (control.value !== optionValue) {
            control.value = optionValue;
            changedControl = control;
          }
          return;
        }

        const radio = Array.from(control.querySelectorAll('input[type="radio"]')).find(
          (input) => input.value === optionValue
        );
        if (radio && !radio.checked) {
          radio.checked = true;
          changedControl = radio;
        }
      });

      if (changedControl) changedControl.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateStickyBar(variantId, source) {
      const stickyBar = this.getStickyBar();
      const select = stickyBar?.querySelector('select[name="id"]');
      if (!select) return false;
      if (select.value === String(variantId)) {
        return source === 'sticky' || source === 'theme';
      }

      select.value = String(variantId);
      if (source !== 'sticky' && source !== 'theme') {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    updateUrl(variantId) {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') === String(variantId)) return;
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({ variantId: String(variantId) }, '', url.toString());
    }

    synchronize(variantId, source = 'custom', updateUrl = true) {
      variantId = String(variantId || '');
      if (this.updating || !this.variantIds.has(variantId)) return;

      const variant = this.variants.find((item) => String(item.id) === variantId);
      if (!variant) return;

      this.updating = true;
      this.currentVariantId = variantId;
      this.updateCustomCards(variantId);
      this.updateMainForm(variantId);
      const stickyHandled = this.updateStickyBar(variantId, source);
      if (!stickyHandled) this.updateNativePicker(variant, source);
      if (updateUrl) this.updateUrl(variantId);
      this.updating = false;
    }

    subscribeToThemeVariantChanges() {
      if (!window.FoxTheme?.pubsub?.subscribe || !FoxTheme.pubsub.PUB_SUB_EVENTS?.variantChange) return;

      this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        if (String(event?.data?.sectionId || '') !== this.sectionId) return;
        const variantId = event?.data?.variant?.id;
        if (variantId) this.synchronize(variantId, 'theme');
      });
    }

    disconnectedCallback() {
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }
  }

  customElements.define('product-variant-sync', ProductVariantSync);

  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('[data-nv-combo-qty]')) return;

      const card = event.target.closest(
        'product-info [data-nv-variant-id], product-info [data-dry-fruit-pack-selector] [data-variant-id], product-info .gpack-card[data-id]'
      );
      if (!card || card.disabled || card.getAttribute('aria-disabled') === 'true') return;

      const productInfo = card.closest('product-info');
      const controller = productInfo?.querySelector(':scope > product-variant-sync');
      const variantId = card.dataset.nvVariantId || card.dataset.variantId || card.dataset.id;
      if (!controller || !variantId) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      controller.synchronize(variantId, 'custom');
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const stickySelect = event.target.closest('sticky-atc-bar select[name="id"]');
      if (!stickySelect) return;

      const stickyBar = stickySelect.closest('sticky-atc-bar');
      const productInfo = document.querySelector(
        `product-info[data-product-id="${CSS.escape(String(stickyBar.dataset.productId || ''))}"]`
      );
      const controller = productInfo?.querySelector(':scope > product-variant-sync');
      if (controller) controller.synchronize(stickySelect.value, 'sticky');
    },
    true
  );
}
